
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Logger } from '@aws-amplify/core';
import * as L from 'leaflet';
import { MachineMarkerLeafletOptions } from '../models/MachineMarkerLeafletOptions';
import { useMachineStore } from '../stores/machineStore';
import { useMenuStore } from '../stores/menuStore';
import { MachineKey } from '../models/MachineKey';


const logger = new Logger('LeafletMapMachineMarker');
@Component({ name: 'LeafletMapMachineMarker' })
export default class LeafletMapMachineMarker extends Vue {
  @Prop() map!: L.Map;
  @Prop() layer!: L.LayerGroup | L.Map;
  @Prop() marker!: MachineMarkerLeafletOptions;
  @Prop() showSelectedMachine!: boolean;
  mapMarker = null as L.Marker | null;
  mapCircle = null as L.Marker | null;
  public machineStore = useMachineStore();
  public menuStore = useMenuStore();

  @Watch('marker')
  onMarkerChange(val: MachineMarkerLeafletOptions, oldVal: MachineMarkerLeafletOptions) {
    if (this.mapMarker && Date.parse(val.UpdateMessage.SentUTC) > Date.parse(oldVal.UpdateMessage.SentUTC)) {
      if (this.mapMarker != null) {
        this.mapMarker.setLatLng(this.marker.position);
        if (val.MarkerOptions.zIndexOffset != null) {
          this.mapMarker.setZIndexOffset(val.MarkerOptions.zIndexOffset as number);
        }
      }

      if (this.mapMarker != null) this.mapMarker.setIcon(this.CreateSvgMarker(this.marker.UpdateMessage.Icon));

      if (
        this.showSelectedMachine &&
        this.mapMarker != null &&
        this.machineStore.selectedMachine != null &&
        this.machineStore.selectedMachine.machineId == val.UpdateMessage.Key.machineId
      ) {
        this.mapCircle?.setLatLng(this.mapMarker.getLatLng());
        this.machineStore.setSelectedMachineMarker(val.UpdateMessage);
        this.map.setView(this.mapMarker.getLatLng());
      } else if (
        this.mapMarker != null &&
        this.machineStore.selectedMachine != null &&
        this.machineStore.selectedMachine.machineId == val.UpdateMessage.Key.machineId
      ) {
        this.map.setView(this.mapMarker.getLatLng());
      }
    }
  }
  private getDataURI(iconBase64: string) {
    return 'data:image/svg+xml;base64, ' + iconBase64;
  }

  @Watch('machineStore.selectedMachine')
  onSelectedMachineChange(value: MachineKey): void {
    if (value == null) {
      if (this.mapCircle != null) {
        this.mapCircle.remove();
      }
    } else {
      if (
        this.showSelectedMachine &&
        this.mapMarker != null &&
        this.machineStore.selectedMachine != null &&
        value != null &&
        value.machineId == this.marker.UpdateMessage.Key.machineId
      ) {
        this.mapMarker.setZIndexOffset(99);
        if (this.mapCircle != null) {
          this.mapCircle.setLatLng(this.mapMarker.getLatLng());
          this.mapCircle.addTo(this.layer);
        } else {
          this.mapCircle = new L.Marker(this.marker.position, this.CalculateCircleSymbol());
          this.mapCircle.addTo(this.layer);
        }

        this.map.setView(this.marker.position);
      } else {
        if (this.mapCircle != null) {
          this.mapCircle.remove();
        }
        if (this.mapMarker != null) this.mapMarker.setZIndexOffset(1);
      }
    }
  }

  private AddSelectedListner() {
    this.mapMarker?.off('click');
    if (this.mapMarker != null) {
      this.mapMarker.on('click', () => {
        this.machineStore.setSelectedMachine(this.marker.Machine);
        this.menuStore.setupMachineMenuState();
      });
    }
  }

  destroyed() {
    this.destroyResources();
  }
  private destroyResources() {
    this.mapMarker?.off('click');
    if (this.mapMarker) {
      this.mapMarker.remove();
    }
    if (this.mapCircle) {
      this.mapCircle.remove();
    }
  }

  deactivated() {
    this.mapMarker?.off('click');
  }

  private CalculateCircleSymbol(): L.MarkerOptions {
    const selectedCircleIcon = L.icon({
      iconUrl: `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" ><circle cx="32" cy="32" r="30" stroke="%23FFFFFF" stroke-width="1" opacity="0.5" fill="%23FFFFFF" /></svg>`,
      iconAnchor: [32, 32],
      iconSize: [64, 64]
    });

    return { icon: selectedCircleIcon };
  }

  private CreateSvgMarker(iconBase64: string): L.Icon {
    const svgstr = atob(iconBase64);
    const parser = new DOMParser();
    const svg = parser.parseFromString(svgstr, 'image/svg+xml');
    const svgTag = svg.getElementsByTagName('svg')[0];

    // svg transform attribute not supported by safari, we must use an inner <g> element for our rotation to work on iOS
    // see https://stackoverflow.com/questions/48248512/svg-transform-rotate180-does-not-work-in-safari-11
    const innerGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    innerGroup.innerHTML = svgTag.innerHTML;

    // set height and width equal so icon is not clipped off when rotated
    svgTag.setAttribute('width', '48');
    svgTag.setAttribute('height', '48');
    // rotate image, scale down so icon is not clipped off at corners, translate icon to center it on gps point
    innerGroup.setAttribute('transform', `scale(0.9, 0.9) translate(1.6 2.4) rotate(${this.marker.UpdateMessage.Location.Location.Bearing} 16 24)`);

    svgTag.innerHTML = '';
    svgTag.appendChild(innerGroup);

    const icon = btoa(new XMLSerializer().serializeToString(svg));

    return new L.Icon({ iconUrl: this.getDataURI(icon), iconSize: [48, 48], iconAnchor: [24, 24] });
  }

  async mounted() {
    await this.$nextTick();
    this.mapMarker = new L.Marker(this.marker.position, this.marker.MarkerOptions);
    this.mapMarker.setIcon(this.CreateSvgMarker(this.marker.UpdateMessage.Icon));

    this.mapMarker.addTo(this.map);
    this.AddSelectedListner();

    if (
      this.showSelectedMachine &&
      this.machineStore.selectedMachine != null &&
      this.machineStore.selectedMachine.machineId == this.marker.UpdateMessage.Key.machineId
    ) {
      this.mapCircle = new L.Marker(this.marker.position, this.CalculateCircleSymbol()).addTo(this.layer);
      this.mapMarker.addTo(this.layer);
      this.map.panTo(this.marker.position);
    }
    logger.debug('Machine Added', this.marker.Machine.machineId);
  }
}
